import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link,
  List,
  ListOrdered,
  Underline,
} from "lucide-react";
import React, { useCallback } from "react";

interface EmailToolbarProps {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  readonly?: boolean;
}

export const EmailToolbar: React.FC<EmailToolbarProps> = ({
  iframeRef,
  readonly = false,
}) => {
  const execCommand = useCallback(
    (command: string, value?: string) => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;

      doc.execCommand(command, false, value);
      // Refocus the iframe after command
      iframe.contentWindow?.focus();
    },
    [iframeRef],
  );

  const insertLink = useCallback(() => {
    const url = prompt("Enter URL:");
    if (url) {
      execCommand("createLink", url);
    }
  }, [execCommand]);

  if (readonly) return null;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1 p-2 border-b border-neutral-200 dark:border-neutral-800 bg-card">
        <ToolbarButton
          onClick={() => execCommand("bold")}
          icon={<Bold className="w-4 h-4" />}
          label="Bold"
        />
        <ToolbarButton
          onClick={() => execCommand("italic")}
          icon={<Italic className="w-4 h-4" />}
          label="Italic"
        />
        <ToolbarButton
          onClick={() => execCommand("underline")}
          icon={<Underline className="w-4 h-4" />}
          label="Underline"
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton
          onClick={() => execCommand("justifyLeft")}
          icon={<AlignLeft className="w-4 h-4" />}
          label="Align Left"
        />
        <ToolbarButton
          onClick={() => execCommand("justifyCenter")}
          icon={<AlignCenter className="w-4 h-4" />}
          label="Align Center"
        />
        <ToolbarButton
          onClick={() => execCommand("justifyRight")}
          icon={<AlignRight className="w-4 h-4" />}
          label="Align Right"
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton
          onClick={() => execCommand("insertUnorderedList")}
          icon={<List className="w-4 h-4" />}
          label="Bullet List"
        />
        <ToolbarButton
          onClick={() => execCommand("insertOrderedList")}
          icon={<ListOrdered className="w-4 h-4" />}
          label="Numbered List"
        />

        <Separator orientation="vertical" className="h-6 mx-1" />

        <ToolbarButton
          onClick={insertLink}
          icon={<Link className="w-4 h-4" />}
          label="Insert Link"
        />
      </div>
    </TooltipProvider>
  );
};

interface ToolbarButtonProps {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  icon,
  label,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClick}
          className="h-8 w-8 p-0"
          aria-label={label}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
};
